import { useMemo, type MouseEvent } from 'react';
import { Collapse, Dropdown } from 'antd';
import { AiOutlinePlus } from 'react-icons/ai';
import ServiceBadge from '../ServiceBadge';
import { ADD_ON_CATEGORY_LABEL, addOnCategory, addOnTone } from '../../constants';
import { AddOnCategory } from '../../../../enums/clientEnums';
import type { AddOnCatalogueItem } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

interface AddOnPickerProps {
  catalogue: AddOnCatalogueItem[];
  /** How many rows each add-on already has; an added one stays in the menu, washed out and disabled. */
  countsById: Map<number, number>;
  disabled?: boolean;
  open: boolean;
  /** The panel the menu mounts expanded on; set when a row outside the menu opens it. */
  initialCategory: AddOnCategory | null;
  onOpenChange: (open: boolean) => void;
  onAdd: (addOn: AddOnCatalogueItem) => void;
}

const dropdownContainer = () => document.body;

const AddOnPicker = ({ catalogue, countsById, disabled, open, initialCategory, onOpenChange, onAdd }: AddOnPickerProps) => {
  // Accordion panels in category order; a category with no add-ons is left out.
  const panels = useMemo(
    () =>
      Object.values(AddOnCategory)
        .map((category) => ({ category, addOns: catalogue.filter((addOn) => addOnCategory(addOn.addonType) === category) }))
        .filter(({ addOns }) => addOns.length > 0),
    [catalogue],
  );

  const handleOptionClick = (event: MouseEvent<HTMLButtonElement>) => {
    const addOn = catalogue.find((item) => item.id === Number(event.currentTarget.dataset.id));
    if (addOn) onAdd(addOn);
    onOpenChange(false);
  };

  const renderMenu = () => (
    <div className={styles.addOnMenuPanel}>
      <h4 className={styles.addOnMenuTitle}>ADD-ONS</h4>
      <Collapse
        accordion
        ghost
        expandIconPosition="start"
        className={styles.addOnAccordion}
        defaultActiveKey={initialCategory ?? undefined}
        items={panels.map(({ category, addOns }) => ({
          key: category,
          label: ADD_ON_CATEGORY_LABEL[category],
          children: addOns.map((addOn) => (
            <button
              key={addOn.id}
              type="button"
              className={styles.addOnOption}
              data-id={addOn.id}
              disabled={countsById.has(addOn.id)}
              onClick={handleOptionClick}
            >
              <ServiceBadge label={addOn.name} tone={addOnTone(addOn.addonType)} />
            </button>
          )),
        }))}
      />
    </div>
  );

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled || panels.length === 0}
      // Unmounting on close is what resets the accordion; its open panel is local to the menu.
      destroyPopupOnHide
      getPopupContainer={dropdownContainer}
      overlayClassName={styles.addOnMenu}
      dropdownRender={renderMenu}
    >
      <button type="button" className={styles.addAddOn} aria-label="Add an add-on">
        <AiOutlinePlus />
      </button>
    </Dropdown>
  );
};

export default AddOnPicker;
